import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Eye, Building2, ScrollText, X, ArrowLeft, ShieldCheck, MapPin, Clock, Unlock } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/brand/Logo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

function Countdown({ end }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (!end) return null;
  const diff = new Date(end) - now;
  if (diff <= 0) return <span className="font-semibold text-red-600">Expired</span>;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return <span className="font-mono font-semibold text-blue-700">{d}d {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}</span>;
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { setImpersonating, logout } = useAuth();
  const [tab, setTab] = useState("businesses");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", owner_email: "", venues: "" });
  const [venueInputs, setVenueInputs] = useState({});

  const { data: businesses = [], isLoading } = useQuery({
    queryKey: ["admin-businesses"],
    queryFn: async () => (await api.get("/admin/businesses")).data,
  });
  const { data: logs = [] } = useQuery({
    queryKey: ["admin-audit"],
    queryFn: async () => (await api.get("/admin/audit-log")).data,
    enabled: tab === "audit",
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-businesses"] });

  const createBusiness = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.owner_email.trim()) { toast.error("Name and owner email required"); return; }
    try {
      await api.post("/admin/businesses", {
        name: form.name.trim(), owner_email: form.owner_email.trim(),
        venues: form.venues.split(",").map((v) => v.trim()).filter(Boolean),
      });
      toast.success("Business created");
      setCreateOpen(false); setForm({ name: "", owner_email: "", venues: "" });
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create"); }
  };

  const delBusiness = async (b) => {
    if (!window.confirm(`Delete ${b.name} and all its data?`)) return;
    await api.delete(`/admin/businesses/${b.business_id}`);
    toast.success("Business deleted");
    refresh();
  };

  const addVenue = async (b) => {
    const name = (venueInputs[b.business_id] || "").trim();
    if (!name) return;
    await api.post(`/admin/businesses/${b.business_id}/venues`, { name });
    setVenueInputs({ ...venueInputs, [b.business_id]: "" });
    toast.success("Venue added");
    refresh();
  };

  const removeVenue = async (b, venue_id) => {
    await api.delete(`/admin/businesses/${b.business_id}/venues/${venue_id}`);
    toast.success("Venue removed");
    refresh();
  };

  const impersonate = async (b) => {
    try {
      await api.post(`/admin/impersonate/${b.business_id}`);
      setImpersonating({ business_id: b.business_id, name: b.name });
      navigate("/dashboard");
    } catch { toast.error("Could not impersonate"); }
  };

  const extendTrial = async (b, days) => {
    try {
      await api.post(`/admin/businesses/${b.business_id}/extend?days=${days}`);
      toast.success(`Extended ${b.name} by ${days} days`);
      refresh();
    } catch { toast.error("Could not extend"); }
  };

  const unlock = async (b) => {
    try {
      await api.post(`/admin/businesses/${b.business_id}/unlock`);
      toast.success(`${b.name} unlocked — full access`);
      refresh();
    } catch { toast.error("Could not unlock"); }
  };

  return (
    <div className="min-h-screen bg-cloud">
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-pulse-navy px-4 sm:px-8">
        <div className="flex items-center gap-4">
          <Logo variant="light" size={24} />
          <span className="hidden rounded bg-white/10 px-2.5 py-1 text-xs font-semibold text-white sm:inline">ADMIN</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/")} className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-white/70 transition-colors hover:text-white"><ArrowLeft size={15} /> Site</button>
          <button onClick={logout} className="rounded-md border border-white/20 px-3 py-1.5 text-sm text-white/80 transition-colors hover:bg-white/10">Sign out</button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl p-4 sm:p-8">
        <div className="flex items-center gap-2"><ShieldCheck className="text-pulse-green" /><h1 className="text-2xl font-bold text-pulse-navy">Developer console</h1></div>
        <p className="mt-1 text-sm text-slate">Provision clients, assign venues and manage every Pulse business.</p>

        <div className="mt-6 flex gap-1 rounded-md border border-slate-200 bg-white p-1 w-fit">
          {[["businesses","Businesses"],["audit","Audit log"]].map(([v,l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`admin-tab-${v}`}
              className={`rounded px-4 py-1.5 text-sm font-medium transition-colors ${tab === v ? "bg-pulse-navy text-white" : "text-slate hover:text-pulse-navy"}`}>{l}</button>
          ))}
        </div>

        {tab === "businesses" && (
          <div className="mt-6 space-y-4">
            <button onClick={() => setCreateOpen(true)} data-testid="admin-new-business-btn"
              className="flex items-center gap-2 rounded-md bg-pulse-green px-5 py-2.5 text-sm font-semibold text-white transition-transform active:scale-[0.97]">
              <Plus size={16} /> Provision new client
            </button>

            {!isLoading && businesses.length > 0 && (
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-pulse-sm" data-testid="dev-product-calendar">
                <div className="flex items-center gap-2 border-b border-slate-100 p-4 text-sm font-semibold text-pulse-navy">
                  <span className="grid h-6 w-6 place-items-center rounded bg-pulse-navy"><Building2 size={13} className="text-pulse-green" /></span>
                  Calendar <span className="font-normal text-slate">· {businesses.length} client{businesses.length === 1 ? "" : "s"}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="dev-calendar-table">
                    <thead><tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate">
                      <th className="px-4 py-2.5">#</th><th className="px-4 py-2.5">Client</th><th className="px-4 py-2.5">Email</th><th className="px-4 py-2.5">WhatsApp</th><th className="px-4 py-2.5">Started</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Actions</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-50">
                      {businesses.map((b, i) => {
                        const start = b.trial_start ? new Date(b.trial_start) : (b.created_at ? new Date(b.created_at) : null);
                        const left = b.trial_end ? Math.max(0, Math.ceil((new Date(b.trial_end) - Date.now()) / 86400000)) : null;
                        return (
                          <tr key={b.business_id} data-testid={`dev-row-${b.business_id}`}>
                            <td className="px-4 py-2.5 text-slate">{i + 1}</td>
                            <td className="px-4 py-2.5 font-medium text-charcoal">{b.owner_name || b.name}</td>
                            <td className="px-4 py-2.5 text-slate">{b.owner_email}</td>
                            <td className="px-4 py-2.5 text-slate">{b.whatsapp || "—"}</td>
                            <td className="px-4 py-2.5 text-slate">{start ? start.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}</td>
                            <td className="px-4 py-2.5">{b.access_state === "active" ? <span className="font-semibold text-pulse-green">Paid / active</span> : b.access_state === "expired" ? <span className="font-semibold text-red-600">Expired</span> : left !== null ? <span className="font-semibold text-blue-700">{left}d trial left</span> : "—"}</td>
                            <td className="px-4 py-2.5">
                              <div className="flex flex-wrap gap-1.5">
                                <button onClick={() => extendTrial(b, 15)} data-testid={`dev-extend15-${b.business_id}`} className="rounded border border-slate-200 px-2 py-1 text-xs font-medium text-pulse-navy hover:bg-cloud">+15d</button>
                                <button onClick={() => extendTrial(b, 7)} className="rounded border border-slate-200 px-2 py-1 text-xs font-medium text-pulse-navy hover:bg-cloud">+7d</button>
                                {b.access_state !== "active" && <button onClick={() => unlock(b)} data-testid={`dev-unlock-${b.business_id}`} className="rounded bg-pulse-navy px-2 py-1 text-xs font-semibold text-white">Unlock</button>}
                                <button onClick={() => impersonate(b)} data-testid={`dev-view-${b.business_id}`} className="rounded border border-slate-200 px-2 py-1 text-xs font-medium text-pulse-navy hover:bg-cloud">View</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {isLoading ? <Skeleton className="h-40 w-full rounded-xl" /> : businesses.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-white py-16 text-center"><Building2 className="mx-auto text-slate/50" size={36} /><p className="mt-3 text-slate">No businesses yet</p></div>
            ) : businesses.map((b) => (
              <div key={b.business_id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-pulse-sm" data-testid={`admin-business-${b.business_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold text-pulse-navy">{b.name}</div>
                    <div className="text-sm text-slate">{b.owner_email}</div>
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate">
                      <span>{b.booking_count} bookings</span><span>·</span><span>{b.employee_count} employees</span><span>·</span>
                      <span className="font-semibold text-pulse-green">{formatINR(b.total_revenue)} revenue</span>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`trial-status-${b.business_id}`}>
                      {b.access_state === "active" ? (
                        <span className="rounded-md bg-pulse-green/10 px-2.5 py-1 text-xs font-semibold text-pulse-green">Active · Full access</span>
                      ) : b.access_state === "trial" ? (
                        <span className="flex items-center gap-1.5 rounded-md bg-blue-50 px-2.5 py-1 text-xs text-blue-700"><Clock size={12} /> Trial ends in <Countdown end={b.trial_end} /></span>
                      ) : b.access_state === "expired" ? (
                        <span className="rounded-md bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600">Trial expired · Locked</span>
                      ) : (
                        <span className="rounded-md bg-cloud px-2.5 py-1 text-xs text-slate">No trial</span>
                      )}
                      <button onClick={() => extendTrial(b, 15)} data-testid={`extend-15-${b.business_id}`} className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-pulse-navy transition-colors hover:bg-cloud">Extend +15d</button>
                      <button onClick={() => extendTrial(b, 7)} data-testid={`extend-7-${b.business_id}`} className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-pulse-navy transition-colors hover:bg-cloud">+7d</button>
                      {b.access_state !== "active" && (
                        <button onClick={() => unlock(b)} data-testid={`unlock-${b.business_id}`} className="flex items-center gap-1 rounded-md bg-pulse-navy px-2.5 py-1 text-xs font-semibold text-white transition-transform active:scale-95"><Unlock size={12} /> Unlock full access</button>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => impersonate(b)} data-testid={`impersonate-${b.business_id}`}
                      className="flex items-center gap-1.5 rounded-md bg-pulse-navy px-3.5 py-2 text-xs font-semibold text-white transition-transform active:scale-95"><Eye size={14} /> View as owner</button>
                    <button onClick={() => delBusiness(b)} className="grid h-8 w-8 place-items-center rounded-md border border-red-200 text-red-600 transition-colors hover:bg-red-50"><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="mt-4 border-t border-slate-100 pt-4">
                  <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate"><MapPin size={13} /> Venues</div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {b.venues.map((v) => (
                      <span key={v.venue_id} className="flex items-center gap-1.5 rounded-md px-3 py-1 text-sm font-medium" style={{ background: `${v.color}18`, color: v.color }}>
                        <span className="h-2 w-2 rounded-full" style={{ background: v.color }} />{v.name}
                        <button onClick={() => removeVenue(b, v.venue_id)} className="ml-1 text-current opacity-50 hover:opacity-100"><X size={12} /></button>
                      </span>
                    ))}
                    <div className="flex items-center gap-1.5">
                      <input value={venueInputs[b.business_id] || ""} onChange={(e) => setVenueInputs({ ...venueInputs, [b.business_id]: e.target.value })}
                        placeholder="Add venue" data-testid={`add-venue-input-${b.business_id}`}
                        className="w-28 rounded-md border border-slate-200 px-2.5 py-1 text-sm outline-none focus:border-pulse-green" />
                      <button onClick={() => addVenue(b)} data-testid={`add-venue-btn-${b.business_id}`} className="grid h-7 w-7 place-items-center rounded-md bg-pulse-navy text-white"><Plus size={14} /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "audit" && (
          <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-pulse-sm">
            <div className="flex items-center gap-2 p-5 pb-3 text-sm font-semibold text-pulse-navy"><ScrollText size={16} /> Admin audit log</div>
            {logs.length === 0 ? <div className="py-12 text-center text-sm text-slate">No activity yet</div> : (
              <div className="divide-y divide-slate-50">
                {logs.map((l) => (
                  <div key={l.log_id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm" data-testid={`audit-${l.log_id}`}>
                    <div><span className="font-medium capitalize text-charcoal">{l.action.replace(/_/g," ")}</span>{l.detail && <span className="text-slate"> · {l.detail}</span>}</div>
                    <div className="text-xs text-slate">{new Date(l.timestamp).toLocaleString("en-IN")}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-xl sm:max-w-md" data-testid="create-business-modal">
          <DialogHeader><DialogTitle className="text-2xl font-bold text-pulse-navy">Provision a client</DialogTitle></DialogHeader>
          <form onSubmit={createBusiness} className="mt-2 space-y-4">
            <div><label className="text-xs font-medium uppercase tracking-wide text-slate">Business name</label>
              <input data-testid="business-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 w-full rounded-md border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-pulse-green" placeholder="Nirvana Farms & Estates" /></div>
            <div><label className="text-xs font-medium uppercase tracking-wide text-slate">Owner email</label>
              <input type="email" data-testid="business-owner-input" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                className="mt-1.5 w-full rounded-md border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-pulse-green" placeholder="owner@email.com" /></div>
            <div><label className="text-xs font-medium uppercase tracking-wide text-slate">Venues (comma-separated)</label>
              <input data-testid="business-venues-input" value={form.venues} onChange={(e) => setForm({ ...form, venues: e.target.value })}
                className="mt-1.5 w-full rounded-md border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-pulse-green" placeholder="Nirvana, Niyara" /></div>
            <button type="submit" data-testid="create-business-submit" className="w-full rounded-md bg-pulse-green px-6 py-3 text-sm font-semibold text-white transition-transform active:scale-[0.98]">Create business</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
